// Package imports:
import React, { useMemo } from "react";
import Bugsnag from "@bugsnag/js";
import { Form, Formik } from "formik";
import * as Yup from 'yup';
// Component imports:
import Button from "../../../ui-elements/Button/Button";
import Input from "../../../ui-elements/Input/Formik/Input";
import Link from "../../../ui-elements/Link/Link";
import ExampleReportButton from "../../ExampleReportButton/ExampleReportButton";
// Service imports:
import { GET_KELDAN_API_URL } from "../../../services/config";
import { isSSN, stripSSN } from "../../../services/utils";
import { useBuyingProcess } from "../../../services/buyhook";
import { ErrorMessages } from "../../../services/errorMessages";
// Type imports:
import { IKeldanApiContact } from "../../../types/KeldanTypes";


const ConstructionsSearch: React.FC = () => {
    const { tryPurchaseItem, tryToBuyItem, resetState, ModalsAndToasts } = useBuyingProcess(async (values: IFormValues) => {
        try {
            const requestQuery = new URLSearchParams({
                Ssn: stripSSN(values.ssn) ?? '',
                Registration: values.skraningarnr,
                reference: values.tilvisun
            });
            const url = `${GET_KELDAN_API_URL()}/Vehicle/ConstructionsSearch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const responseBody: IKeldanApiContact = await response.json();
                    const { message } = responseBody;
                    
                    if (message === ErrorMessages.NoCard) {
                        resetState('no card');
                    }
                    else if (message) {
                        resetState({
                            type: 'alert',
                            headText: message
                        });
                    }
                    else {
                        resetState('bad request');
                    }
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error');
        }
    }, 'vinnuvélaskrá');

    const subFormComponent = useMemo(() => {
        return ((props: { values: IFormValues }) => (
            <SubFormComponent
                values={props.values}
                loading={(tryToBuyItem !== null)}
            />
        ))
    }, [ tryToBuyItem ]);

    return (
        <div className="Okutaekjaskra">
            <div className="section__body">
                <div className='KCL_vehicle-search-form'>
                    <h3>Uppfletting í Vinnuvélaskrá</h3>
                    <div className='search-form-description'>
                        <span>Með því að slá inn skráningarnúmer eða kennitölu eiganda er hægt að kaupa upplýsingar um vinnuvélar. <Link linkSize="18" href="/Verdskra#verdskra">Sjá verðskrá.</Link></span>
                    </div>
                    <Formik
                        initialValues={{
                            ssn: '',
                            skraningarnr: '',
                            tilvisun: '',
                        }}
                        validationSchema={Yup.object({
                            ssn: Yup.string()
                                    .matches(/^([\d]{6}[-][\d]{4})|^(\d+)$/, 'Kennitala má einungis innihalda tölustafi')
                                    .min(10, 'Kennitala þarf að innihalda 10 tölustafi')
                                    .max(11, 'Kennitala má ekki vera meira en 10 tölustafir')
                                    .test('ssn-test', 'Ógild kennitala', (value) => isSSN(value, true)),
                            skraningarnr: Yup.string(),
                            tilvisun: Yup.string(),
                        })}
                        onSubmit={(values) => tryPurchaseItem(values)}
                        component={subFormComponent}
                    />
                    <ExampleReportButton size='lg' reportType='construction' />
                    <ExampleReportButton size='lg' reportType='constructions-owned' />
                </div>
            </div>
            <ModalsAndToasts />
        </div>
    );
}

export default ConstructionsSearch;

interface IFormValues {
    ssn: string,
    skraningarnr: string,
    tilvisun: string,
}

interface ISubFormProps {
    values: IFormValues,
    loading: boolean,
}

const SubFormComponent: React.FC<ISubFormProps> = ({
    values,
    loading,
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                className='inputKennitala'
                                label="Kennitala"
                                name="ssn"
                                id="ssn"
                                value={values.ssn}
                                disabled={(values.skraningarnr !== '')}
                            />
                        </div>
                        <div className="form__col">
                            <Input
                                className='inputSkraningarnr'
                                label="Skráningarnúmer"
                                name="skraningarnr"
                                id="skraningarnr"
                                value={values.skraningarnr}
                                disabled={(values.ssn !== '')}
                            />
                        </div>
                        <div className="form__col tilvisunInput">
                            <Input
                                label="Tilvísun á reikning"
                                name="tilvisun"
                                id="tilvisun"
                                value={values.tilvisun}
                            />
                        </div>
                        <div className="form__col actions">
                            <div className="form__actions">
                                <Button
                                    style={{'margin': '0px'}}
                                    showLoader={loading}
                                    buttonType="buy"
                                    size="lg"
                                    disabled={(values.ssn === '' && values.skraningarnr === '')}
                                >
                                    Kaupa
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    );
}

